import { Injectable } from '@angular/core';
import { LogService } from './logger/log.service';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { WifiInfoPlugin, WifiInfo } from '@paymash/capacitor-wifi-info-plugin';
import { PlatformService } from './platform/platform.service';
import { NgZoneService } from './ng-zone/ng-zone.service';
import { Network, ConnectionStatus } from '@capacitor/network';
import { NativeOnly } from '@shared/decorators/native-only.decor';

interface WifiDataInterface {
  GatewayIP: string;
  SSID: string;
  BSSID: string;
  IPAddress: string;
}

export class WifiData implements WifiDataInterface {
  BSSID: string;
  GatewayIP: string;
  IPAddress: string;
  SSID: string;

  constructor(data?: WifiInfo) {
    this.BSSID = data?.bssid || '';
    this.GatewayIP = data?.gatewayIp;
    this.IPAddress = data?.ipAddress;
    this.SSID = data?.ssid;
  }

  isEqual(data: WifiData) {
    return JSON.stringify(data) === JSON.stringify(this);
  }
}

@Injectable()
export class NetworkService {
  public internetStatusChangeEvent: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  readonly url: string = 'https://app.mypaymash.com/robots.txt';
  readonly checkWifiDataInterval = 5000;
  private isOffline: boolean = false;
  private wifiPollingSub?: Subscription;

  public get isOfflineSubject() {
    return this.internetStatusChangeEvent;
  }

  private wifiData: WifiData = new WifiData();
  private readonly logger = this.logService.createLogger('NetworkService');

  constructor(
    private logService: LogService,
    private ngZoneService: NgZoneService,
    private platformService: PlatformService
  ) {}

  public getConnectionStatus(): boolean {
    return this.isOffline;
  }

  public setConnectionStatus(isOffline: boolean) {
    if (this.isOffline !== isOffline) {
      this.logger.info('setConnectionStatus:isOffline', { isOffline });
      this.isOffline = isOffline;
      this.internetStatusChangeEvent.next(this.isOffline);
      this.logWifiInfo().then();
    }
  }

  public startNetworkMonitoring() {
    Network.getStatus().then((status: ConnectionStatus) => {
      switch (status.connected) {
        case true:
          this.logger.info('network:ONLINE');
          this.logWifiInfo().then();
          break;
        case false:
          this.logger.info('network:OFFLINE');
          this.logWifiInfo().then();
          this.setConnectionStatus(true);
      }
    });

    // Replaces the cordova 'offline' listener dropped in 2.14.0; going back online stays confirmed by the /status poll.
    void Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
      // Type 'none' only: Android reports unvalidated networks as connected:false.
      if (!status.connected && status.connectionType === 'none') {
        this.logger.info('network:OFFLINE');
        this.setConnectionStatus(true);
      }
    });

    if (this.platformService.isNativePlatform) {
      this.wifiPollingSub?.unsubscribe();
      this.wifiPollingSub = timer(0, this.checkWifiDataInterval, this.ngZoneService.leaveNgZoneRxjs()).subscribe(() => {
        this.compareWifiDataAndLog();
      });
    }
  }

  public stopNetworkMonitoring() {
    this.wifiPollingSub?.unsubscribe();
    this.wifiPollingSub = undefined;
  }

  private async checkConnection(): Promise<string> {
    const networkState: ConnectionStatus = await Network.getStatus();

    try {
      const states = {};

      states['unknown'] = 'Unknown connection';
      states['wifi'] = 'WiFi connection';
      states['cellular'] = 'Cell generic connection';
      states['none'] = 'No network connection';
      // Deprecated states:
      // states[Connection.CELL_3G] = 'Cell 3G connection';
      // states[Connection.CELL_4G] = 'Cell 4G connection';
      // states[Connection.ETHERNET] = 'Ethernet connection';

      return states[networkState.connectionType];
    } catch (e) {
      return networkState.connectionType;
    }
  }

  @NativeOnly()
  private async logWifiInfo() {
    const connectionType: string = await this.checkConnection();
    this.logger.debug('logWifiInfo:connectionType', { connectionType });
    this.getWifiData()
      .then((info: WifiData) => {
        this.logger.info('logWifiInfo:WifiDataPlugin:info', { info });
      })
      .catch((error: unknown) => {
        this.logger.error({ error }, 'logWifiInfo:getWifiData:error');
      });
  }

  private async getWifiData(): Promise<WifiData> {
    if (this.platformService.isNativePlatform) {
      return WifiInfoPlugin.getData().then((info: WifiInfo) => new WifiData(info));
    }
    throw new Error('getWifiData:This method is only available on native platforms');
  }

  private async compareWifiDataAndLog() {
    try {
      const info: WifiData = await this.getWifiData();
      if (!this.wifiData.isEqual(info)) {
        this.logger.info('compareWifiDataAndLog:WifiDataPlugin:info', { info });
        this.wifiData = info;
      }
    } catch (error) {
      this.logger.error({ error }, 'compareWifiDataAndLog:getWifiData:error');
    }
  }
}
